import { Ref, ref } from 'vue';

/** Drag and Drop APIでdataTransferに保持するキー */
type DragKey = string;

/** 前方へ移動する時の挿入位置補正 */
const FORWARD_MOVE_OFFSET = 1;

/** native DnDで並び替える時の前後関係 */
export interface NativeDragReorderContext<T> {
  draggedItem: T;
  targetItem: T;
  draggedKey: DragKey;
  targetKey: DragKey;
  fromIndex: number;
  toIndex: number;
  items: T[];
}

/** useNativeDragReorderの初期化パラメータ */
export interface UseNativeDragReorderOptions<T> {
  /** 並び替え対象の配列 */
  items: Ref<T[]>;
  /** DnDで識別するキーを返す */
  getKey: (item: T) => DragKey;
  /** falseを返すとdropをキャンセルする */
  canDrop?: (context: NativeDragReorderContext<T>) => boolean;
  /** 並び替え後に実行するコールバック */
  onReorder?: (context: NativeDragReorderContext<T>) => void;
  /** ドラッグプレビューの背景色 */
  dragImageBackgroundColor?: string;
}

/** useNativeDragReorderの戻り値 */
export interface UseNativeDragReorderReturn<T> {
  /** ドラッグ中の項目キー */
  draggingKey: Ref<DragKey | null>;
  /** ドラッグオーバー中の項目キー */
  dragOverKey: Ref<DragKey | null>;
  /** 末尾のドロップ位置にドラッグオーバー中かどうか */
  isDragOverEnd: Ref<boolean>;
  /** ドラッグ開始 */
  handleDragStart: (event: DragEvent, item: T) => void;
  /** ドラッグ終了 */
  handleDragEnd: () => void;
  /** ドラッグオーバー */
  handleDragOver: (event: DragEvent, item: T) => void;
  /** ドラッグリーブ */
  handleDragLeave: (event: DragEvent, item: T) => void;
  /** 末尾ドロップ位置のドラッグオーバー */
  handleDragOverEnd: (event: DragEvent) => void;
  /** 末尾ドロップ位置のドラッグリーブ */
  handleDragLeaveEnd: () => void;
  /** ドロップ */
  handleDrop: (event: DragEvent, item: T) => void;
  /** 末尾へドロップ */
  handleDropToEnd: (event: DragEvent) => void;
  /** ドラッグ状態をリセット */
  resetDragState: () => void;
  /** 対象項目がドラッグ中かどうか */
  isDragging: (item: T) => boolean;
  /** 対象項目にドラッグオーバー中かどうか */
  isDragOver: (item: T) => boolean;
  /** 対象項目の前に挿入予定かどうか */
  isDropTarget: (item: T) => boolean;
}

/**
 * HTML標準のDrag and Drop APIで配列の並び替えを扱うcomposable
 * @param options - 並び替え対象とキー取得関数
 * @returns native DnD用の状態とイベントハンドラー
 */
export const useNativeDragReorder = <T>(options: UseNativeDragReorderOptions<T>): UseNativeDragReorderReturn<T> => {
  const { items, getKey, canDrop, onReorder, dragImageBackgroundColor } = options;
  const draggingKey = ref<DragKey | null>(null);
  const dragOverKey = ref<DragKey | null>(null);
  const isDragOverEnd = ref(false);

  /**
   * ドラッグ状態を初期化する
   */
  const resetDragState = (): void => {
    draggingKey.value = null;
    dragOverKey.value = null;
    isDragOverEnd.value = false;
  };

  /**
   * キーに一致する項目の位置を返す
   * @param key - 項目キー
   * @returns 項目の位置
   */
  const findItemIndex = (key: DragKey): number => items.value.findIndex((item) => getKey(item) === key);

  /**
   * dataTransferまたは状態からドラッグ中の項目キーを取得する
   * @param event - dragイベント
   * @returns ドラッグ中の項目キー
   */
  const getDraggedKey = (event: DragEvent): DragKey | undefined => draggingKey.value ?? event.dataTransfer?.getData('text/plain');

  /**
   * 並び替えに必要なコンテキストを組み立てる
   * @param draggedKey - ドラッグ中の項目キー
   * @param targetKey - ドロップ先の項目キー
   * @param toIndexOverride - ドロップ先の位置
   * @returns 並び替えコンテキスト
   */
  const buildContext = (draggedKey: DragKey, targetKey: DragKey, toIndexOverride?: number): NativeDragReorderContext<T> | null => {
    const fromIndex = findItemIndex(draggedKey);
    const targetIndex = findItemIndex(targetKey);
    const toIndex = toIndexOverride ?? targetIndex;

    if (fromIndex === -1 || targetIndex === -1) return null;

    return {
      draggedItem: items.value[fromIndex],
      targetItem: items.value[targetIndex],
      draggedKey,
      targetKey,
      fromIndex,
      toIndex,
      items: items.value,
    };
  };

  /**
   * ドラッグプレビュー用の要素を作成する
   * @param sourceElement - ドラッグ元要素
   * @returns ドラッグプレビュー要素
   */
  const createDragImageElement = (sourceElement: HTMLElement): HTMLElement => {
    const sourceRect = sourceElement.getBoundingClientRect();
    const sourceStyle = window.getComputedStyle(sourceElement);
    const dragImageElement = sourceElement.cloneNode(true) as HTMLElement;

    dragImageElement.style.position = 'fixed';
    dragImageElement.style.top = `${sourceRect.top}px`;
    dragImageElement.style.left = `${sourceRect.left}px`;
    dragImageElement.style.width = `${sourceElement.offsetWidth}px`;
    dragImageElement.style.height = `${sourceElement.offsetHeight}px`;
    dragImageElement.style.boxSizing = sourceStyle.boxSizing;
    dragImageElement.style.display = sourceStyle.display;
    dragImageElement.style.gridTemplateColumns = sourceStyle.gridTemplateColumns;
    dragImageElement.style.alignItems = sourceStyle.alignItems;
    dragImageElement.style.columnGap = sourceStyle.columnGap;
    dragImageElement.style.padding = sourceStyle.padding;
    dragImageElement.style.border = sourceStyle.border;
    dragImageElement.style.borderRadius = sourceStyle.borderRadius;
    dragImageElement.style.backgroundColor = dragImageBackgroundColor ?? '#ffffff';
    dragImageElement.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.16)';
    dragImageElement.style.opacity = '1';
    dragImageElement.style.pointerEvents = 'none';
    dragImageElement.style.zIndex = '2147483647';
    dragImageElement.style.setProperty('--condition-field-result-width', sourceStyle.getPropertyValue('--condition-field-result-width'));
    dragImageElement.style.setProperty('--condition-field-actions-width', sourceStyle.getPropertyValue('--condition-field-actions-width'));

    return dragImageElement;
  };

  /**
   * ネイティブのドラッグプレビューを設定する
   * @param event - dragstartイベント
   */
  const setDragImage = (event: DragEvent): void => {
    if (!event.dataTransfer || !(event.currentTarget instanceof HTMLElement)) return;

    const dragImageElement = createDragImageElement(event.currentTarget);
    document.body.appendChild(dragImageElement);
    event.dataTransfer.setDragImage(dragImageElement, 0, dragImageElement.offsetHeight / 2);
    window.requestAnimationFrame(() => {
      dragImageElement.remove();
    });
  };

  /**
   * drop操作を実行できるか判定する
   * @param context - 並び替えコンテキスト
   * @returns drop可能な場合true
   */
  const canDropByContext = (context: NativeDragReorderContext<T>): boolean => canDrop?.(context) ?? true;

  /**
   * 配列内の項目を指定位置へ移動する
   * @param context - 並び替えコンテキスト
   */
  const reorderItems = (context: NativeDragReorderContext<T>): void => {
    const [draggedItem] = items.value.splice(context.fromIndex, 1);
    const adjustedTargetIndex = context.fromIndex < context.toIndex ? context.toIndex - FORWARD_MOVE_OFFSET : context.toIndex;
    items.value.splice(adjustedTargetIndex, 0, draggedItem);
    onReorder?.(context);
  };

  /**
   * ドラッグ開始時に項目キーを保持する
   * @param event - dragstartイベント
   * @param item - ドラッグする項目
   */
  const handleDragStart = (event: DragEvent, item: T): void => {
    const key = getKey(item);
    draggingKey.value = key;

    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', key);
      setDragImage(event);
    }
  };

  /**
   * ドラッグ終了時に状態を戻す
   */
  const handleDragEnd = (): void => {
    resetDragState();
  };

  /**
   * ドロップ可能にし、現在のドロップ先候補を保持する
   * @param event - dragoverイベント
   * @param item - ドロップ先候補の項目
   */
  const handleDragOver = (event: DragEvent, item: T): void => {
    event.preventDefault();
    dragOverKey.value = getKey(item);
    isDragOverEnd.value = false;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };

  /**
   * ドロップ先候補から離れた時にハイライト状態を戻す
   * @param event - dragleaveイベント
   * @param item - 離脱した項目
   */
  const handleDragLeave = (event: DragEvent, item: T): void => {
    const currentTarget = event.currentTarget;
    const relatedTarget = event.relatedTarget;

    if (currentTarget instanceof HTMLElement && relatedTarget instanceof Node && currentTarget.contains(relatedTarget)) {
      return;
    }

    const key = getKey(item);
    if (dragOverKey.value === key) {
      dragOverKey.value = null;
    }
  };

  /**
   * 末尾のドロップ位置を有効化する
   * @param event - dragoverイベント
   */
  const handleDragOverEnd = (event: DragEvent): void => {
    event.preventDefault();
    dragOverKey.value = null;
    isDragOverEnd.value = true;

    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'move';
    }
  };

  /**
   * 末尾のドロップ位置から離れた時に状態を戻す
   */
  const handleDragLeaveEnd = (): void => {
    isDragOverEnd.value = false;
  };

  /**
   * ドロップ先の位置へ配列要素を移動する
   * @param event - dropイベント
   * @param item - ドロップ先の項目
   */
  const handleDrop = (event: DragEvent, item: T): void => {
    event.preventDefault();
    event.stopPropagation();

    const targetKey = getKey(item);
    const draggedKey = getDraggedKey(event);

    if (!draggedKey || draggedKey === targetKey) {
      resetDragState();
      return;
    }

    const context = buildContext(draggedKey, targetKey);
    if (!context) {
      resetDragState();
      return;
    }

    if (!canDropByContext(context)) {
      resetDragState();
      return;
    }

    reorderItems(context);
    resetDragState();
  };

  /**
   * ドラッグ中の項目を配列の末尾へ移動する
   * @param event - dropイベント
   */
  const handleDropToEnd = (event: DragEvent): void => {
    event.preventDefault();
    event.stopPropagation();

    const draggedKey = getDraggedKey(event);
    if (!draggedKey) {
      resetDragState();
      return;
    }

    const lastIndex = items.value.length - 1;
    if (lastIndex < 0) {
      resetDragState();
      return;
    }

    const lastItem = items.value[lastIndex];
    const context = buildContext(draggedKey, getKey(lastItem), items.value.length);
    if (!context || context.fromIndex === lastIndex) {
      resetDragState();
      return;
    }

    if (!canDropByContext(context)) {
      resetDragState();
      return;
    }

    reorderItems(context);
    resetDragState();
  };

  /**
   * 対象項目がドラッグ中かどうかを判定する
   * @param item - 判定する項目
   * @returns ドラッグ中の場合true
   */
  const isDragging = (item: T): boolean => draggingKey.value === getKey(item);
  /**
   * 対象項目がドロップ先候補かどうかを判定する
   * @param item - 判定する項目
   * @returns ドロップ先候補の場合true
   */
  const isDragOver = (item: T): boolean => dragOverKey.value === getKey(item);
  /**
   * 対象項目の前に挿入予定かどうかを判定する
   * @param item - 判定する項目
   * @returns 対象項目の前に挿入予定の場合true
   */
  const isDropTarget = (item: T): boolean => isDragOver(item);

  return {
    draggingKey,
    dragOverKey,
    isDragOverEnd,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDragLeave,
    handleDragOverEnd,
    handleDragLeaveEnd,
    handleDrop,
    handleDropToEnd,
    resetDragState,
    isDragging,
    isDragOver,
    isDropTarget,
  };
};
